import { Box, Card, Divider, Heading, Status, StatusBadge, StyledIcon, TextBlock } from "@bcc-ui/ui-components";
import { ArcElement, Chart, Legend, Tooltip, TooltipItem } from "chart.js";
import classNames from "classnames";
import React from "react";
import { Pie } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import style from "./risk-distribution-card.scss";
import { Shield } from "components/icons/Shield";
import { Status as bmpStatus, StatusLabel } from "components/status-label/StatusLabel";
import { RiskLevel } from "domain/asset-management/assetFilterConstants";
import { RiskSummary } from "services/asset-management/AssetService";
import { resolveColor } from "utils/commonFunctions";

Chart.register(ArcElement, Tooltip, Legend);

const createRiskLevelClassNameMap = (
    highClassName: string,
    mediumClassName: string,
    lowClassName: string
): Record<RiskLevel, string> => ({
    [RiskLevel.HIGH]: highClassName,
    [RiskLevel.MEDIUM]: mediumClassName,
    [RiskLevel.LOW]: lowClassName,
    [RiskLevel.NOT_AVAILABLE]: lowClassName,
});

const riskBadgeClassName = createRiskLevelClassNameMap(style.highRiskBadge, style.mediumRiskBadge, style.lowRiskBadge);
const riskTextClassName = createRiskLevelClassNameMap(style.highRiskCount, style.mediumRiskCount, style.lowRiskCount);

interface Props {
    data: RiskSummary;
    hideHeading?: boolean;
    customContent?: React.ReactNode;
    horizontalDirection?: boolean;
    highRiskLabel?: string;
    mediumRiskLabel?: string;
    lowRiskLabel?: string;
}

export const RiskDistributionCard = (props: Props) => {
    const { t } = useTranslation();

    const cardClasses = classNames(style.card, {
        [style.horizontal]: props.horizontalDirection,
    });

    const contentClasses = classNames(style.content, {
        [style.horizontal]: props.horizontalDirection,
    });

    const highRiskLabel = props.highRiskLabel ?? t("AssetManagement.overview.riskLevelSummaryCards.highRisk");
    const mediumRiskLabel = props.mediumRiskLabel ?? t("AssetManagement.overview.riskLevelSummaryCards.mediumRisk");
    const lowRiskLabel = props.lowRiskLabel ?? t("AssetManagement.overview.riskLevelSummaryCards.lowRisk");

    const getTooltipContent = (count: number) => {
        if (count === 1) {
            return `${count} ${t("AssetManagement.asset").toLowerCase()}`;
        }

        return `${count} ${t("AssetManagement.assets").toLowerCase()}`;
    };

    const data = {
        labels: [highRiskLabel, mediumRiskLabel, lowRiskLabel],
        datasets: [
            {
                data: [props.data.totalHigh, props.data.totalMedium, props.data.totalLow],
                backgroundColor: [
                    resolveColor("--app-high-risk-level-color"),
                    resolveColor("--app-medium-risk-level-color"),
                    resolveColor("--app-low-risk-level-color"),
                ],
                borderWidth: 0,
            },
        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            datalabels: {
                display: false,
            },
            tooltip: {
                backgroundColor: resolveColor("--theme-background-layer"),
                titleColor: resolveColor("--theme-text-primary"),
                bodyColor: resolveColor("--theme-text-primary"),
                titleFont: { family: "Lato Medium, Arial, Helvetica, sans-serif", size: 14, weight: "bold" as const },
                bodyFont: { family: "Lato Medium, Arial, Helvetica, sans-serif", size: 14 },
                padding: 12,
                cornerRadius: 4,
                displayColors: true,
                callbacks: {
                    label: (tooltipItem: TooltipItem<"pie">) => getTooltipContent(tooltipItem.parsed),
                },
            },
        },
    };

    const riskLabels = [
        {
            risklevel: RiskLevel.HIGH,
            count: props.data.totalHigh,
            bmpStatus: bmpStatus.ERROR,
            badgeStatus: Status.ERROR,
            label: highRiskLabel,
        },
        {
            risklevel: RiskLevel.MEDIUM,
            count: props.data.totalMedium,
            bmpStatus: bmpStatus.WARNING,
            badgeStatus: Status.WARNING,
            label: mediumRiskLabel,
        },
        {
            risklevel: RiskLevel.LOW,
            count: props.data.totalLow,
            bmpStatus: bmpStatus.SUCCESS,
            badgeStatus: Status.SUCCESS,
            label: lowRiskLabel,
        },
    ];

    const orderedRiskLabels = [
        ...riskLabels.filter((riskLabel) => riskLabel.count > 0),
        ...riskLabels.filter((riskLabel) => riskLabel.count === 0),
    ];

    const hasRiskIssues = props.data.totalHigh + props.data.totalMedium > 0;

    const shieldIcon = <StyledIcon size={200} icon={<Shield />} backgroundColor={"--app-shield-backgroundColor"} />;

    return (
        <Card padding={"None"} dataType="RISK_DISTRIBUTION" className={cardClasses}>
            {!props.hideHeading && (
                <>
                    <Box
                        paddingTop={"Xs"}
                        paddingLeft={"Md"}
                        paddingRight={"Md"}
                        paddingBottom={"Xxs"}
                        verticalAlignment={"Center"}
                        justifyContent={"SpaceBetween"}
                    >
                        <Heading variant="SUBTITLE_1" disableTopSpacing disableBottomSpacing>
                            {t("AssetManagement.risk.riskDistributionChart.heading")}
                        </Heading>
                        <TextBlock className={style.secondaryText}>
                            {props.data.total === 1
                                ? `${props.data.total} ${t("AssetManagement.asset").toLowerCase()}`
                                : `${props.data.total} ${t("AssetManagement.assets").toLowerCase()}`}
                        </TextBlock>
                    </Box>

                    <Box paddingBottom="Xxs">
                        <Divider />
                    </Box>
                </>
            )}
            <Box className={contentClasses}>
                {!hasRiskIssues &&
                    (!props.customContent ? (
                        <Box
                            columnLayout
                            horizontalAlignment="Center"
                            verticalAlignment="Center"
                            paddingTop={"Xs"}
                            paddingLeft={"Sm"}
                            paddingRight={"Sm"}
                            paddingBottom={"Lg"}
                            gap={"Sm"}
                        >
                            {shieldIcon}
                            <Box columnLayout horizontalAlignment="Center" verticalAlignment="Center" gap={"Xxxs"}>
                                <Heading variant={"h2"}>
                                    {t("AssetManagement.overview.riskLevelSummaryCards.noRiskIssues")}
                                </Heading>
                                <TextBlock alignCenter disableBottomSpacing className={style.secondaryText}>
                                    {t("AssetManagement.overview.riskLevelSummaryCards.nothingToReview")}
                                </TextBlock>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <Box horizontalAlignment="Center">{shieldIcon}</Box>
                            {props.customContent}
                        </>
                    ))}

                {hasRiskIssues && (
                    <>
                        <Box horizontalAlignment="Center">
                            <Box horizontalAlignment={"Center"} className={style.chartContainer}>
                                <Pie data={data} options={options} />
                            </Box>
                        </Box>
                        {!props.customContent ? (
                            <Box paddingTop="Lg" columnLayout gap={"Sm"} className={style.labelsContainer}>
                                {/* TODO BCC-6838 Add icon showing trend to each StatusLabel after API support this */}

                                {orderedRiskLabels.map((riskLabel) => (
                                    <StatusLabel
                                        key={riskLabel.risklevel}
                                        status={riskLabel.bmpStatus}
                                        dataType={riskLabel.risklevel}
                                        className={riskLabel.count === 0 ? style.disabled : undefined}
                                    >
                                        <StatusBadge
                                            value={riskLabel.label}
                                            status={riskLabel.badgeStatus}
                                            className={riskBadgeClassName[riskLabel.risklevel]}
                                        />
                                        <strong className={riskTextClassName[riskLabel.risklevel]}>
                                            {riskLabel.count}
                                        </strong>
                                    </StatusLabel>
                                ))}
                            </Box>
                        ) : (
                            props.customContent
                        )}
                    </>
                )}
            </Box>
        </Card>
    );
};
